import { useEffect, useState } from 'react';
import type { DeviceSummary } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';

interface Props {
  readonly session: StoredSession;
  readonly selected: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
  /** Frames already accounted for — an album's current publications, say. */
  readonly exclude?: readonly string[];
  readonly emptyText?: string;
}

/**
 * Which frames a gesture lands on.
 *
 * Only frames this account may actually send to are offered. Showing the rest
 * greyed out would be honest about what exists but useless about what to do,
 * and the server would refuse them anyway — the permission check is there, not
 * here, and this list is only ever the polite version of it.
 */
export const FramePicker = ({ session, selected, onChange, exclude = [], emptyText }: Props) => {
  const [frames, setFrames] = useState<readonly DeviceSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    api
      .listDevices(session.token)
      .then((result) => live && setFrames(result.devices.filter((d) => d.permissions.canSend)))
      .catch(() => undefined)
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [session.token]);

  const offered = frames.filter((frame) => !exclude.includes(frame.id));

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((entry) => entry !== id) : [...selected, id]);

  if (loading) return <p className="note">Loading your frames</p>;
  if (offered.length === 0) {
    return <p className="note">{emptyText ?? 'No frames you can send to.'}</p>;
  }

  return (
    <ul className="chips">
      {offered.map((frame) => {
        const on = selected.includes(frame.id);
        return (
          <li key={frame.id}>
            <button
              type="button"
              className="chip"
              aria-pressed={on}
              onClick={() => toggle(frame.id)}
            >
              <span className="chip-name">{frame.label}</span>
              {/* Said up front rather than after the fact: sending to a frame
                  you are a guest on usually means waiting for somebody. */}
              {!frame.permissions.autoAccept && <span className="chip-note">needs approval</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
};
